import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bus, Wrench, User, AlertCircle, Clock, CheckCircle, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import SendToMechanicDialog from '../SendToMechanicDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function RepairQueue() {
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBusId, setSelectedBusId] = useState('');
  const [selectedBusName, setSelectedBusName] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/buses/repair-overview`, getAuthHeaders());
      setBuses(res.data);
    } catch {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (busId, busName) => {
    setSelectedBusId(busId);
    setSelectedBusName(busName);
    setDialogOpen(true);
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) fetchData();
  };

  const needsWork = buses.filter(b => b.expired_count > 0 || b.active_tasks_count > 0);
  const okBuses = buses.filter(b => b.expired_count === 0 && b.active_tasks_count === 0);
  const totalExpired = buses.reduce((s, b) => s + b.expired_count, 0);
  const totalCritical = buses.reduce((s, b) => s + b.critical_count, 0);
  const totalActiveTasks = buses.reduce((s, b) => s + b.active_tasks_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="repair-queue-page">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-3">
            <p className="text-xs text-red-100">Требуют внимания</p>
            <p className="text-2xl font-bold">{needsWork.length}</p>
            <p className="text-[10px] text-red-200">из {buses.length} автобусов</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-3">
            <p className="text-xs text-orange-100">Просрочено</p>
            <p className="text-2xl font-bold">{totalExpired}</p>
            <p className="text-[10px] text-orange-200">{totalCritical} критических</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-3">
            <p className="text-xs text-blue-100">Активных задач</p>
            <p className="text-2xl font-bold">{totalActiveTasks}</p>
            <p className="text-[10px] text-blue-200">у механиков</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-3">
            <p className="text-xs text-green-100">В порядке</p>
            <p className="text-2xl font-bold">{okBuses.length}</p>
            <p className="text-[10px] text-green-200">автобусов</p>
          </CardContent>
        </Card>
      </div>

      {/* Buses needing work */}
      {needsWork.length > 0 && (
        <div className="space-y-3">
          {needsWork.map(bus => (
            <Card
              key={bus.bus_id}
              className={`overflow-hidden border-l-4 ${
                bus.critical_count > 0 ? 'border-l-red-500' : 'border-l-orange-400'
              }`}
              data-testid={`repair-bus-${bus.bus_id}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3 text-base flex-wrap">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    bus.critical_count > 0 ? 'bg-red-100' : 'bg-orange-100'
                  }`}>
                    <Bus className={`w-5 h-5 ${bus.critical_count > 0 ? 'text-red-600' : 'text-orange-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-bold">{bus.bus_name}</span>
                    <span className="ml-2 text-sm text-gray-400 font-mono">{bus.bus_plate}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {bus.expired_count > 0 && (
                      <Badge variant={bus.critical_count > 0 ? "destructive" : "outline"} className="text-xs">
                        {bus.expired_count} просрочен{bus.expired_count === 1 ? '' : 'о'}
                      </Badge>
                    )}
                    {bus.active_tasks_count > 0 && (
                      <Badge className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">
                        <Wrench className="w-3 h-3 mr-1" />
                        {bus.active_tasks_count} в работе
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      onClick={() => openDialog(bus.bus_id, bus.bus_name)}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid={`send-mechanic-${bus.bus_id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" />
                      Механику
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {/* Expired params */}
                {bus.expired_params.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {bus.expired_params.map((p, i) => (
                      <span
                        key={i}
                        className={`px-2 py-1 text-xs rounded-lg flex items-center gap-1 ${
                          p.is_critical
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-orange-50 text-orange-700 border border-orange-200'
                        }`}
                      >
                        {p.is_critical ? <AlertCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                        {p.parameter_name}
                        <span className="text-[10px] opacity-70">({p.status_text})</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* Active tasks */}
                {bus.active_tasks.length > 0 && (
                  <div className="space-y-1.5">
                    {bus.active_tasks.map(task => (
                      <div key={task.id} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg text-sm">
                        <Wrench className="w-3.5 h-3.5 text-blue-500" />
                        <User className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-700">{task.mechanic_name}</span>
                        {task.is_important && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700">ВАЖНО</span>
                        )}
                        {task.admin_note && (
                          <span className="text-xs text-gray-400 truncate flex-1">{task.admin_note}</span>
                        )}
                        <span className="text-xs text-gray-400 ml-auto">
                          <Clock className="w-3 h-3 inline mr-0.5" />
                          {new Date(task.created_at).toLocaleDateString('ru-RU')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* OK buses */}
      {okBuses.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Автобусы в порядке ({okBuses.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {okBuses.map(bus => (
              <Card key={bus.bus_id} className="border-l-4 border-l-green-400" data-testid={`ok-bus-${bus.bus_id}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bus className="w-4 h-4 text-green-500" />
                    <span className="font-medium text-sm">{bus.bus_name}</span>
                    <span className="text-xs text-gray-400 font-mono">{bus.bus_plate}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openDialog(bus.bus_id, bus.bus_name)}
                    className="text-gray-400 hover:text-orange-600 h-8"
                    data-testid={`send-ok-${bus.bus_id}`}
                  >
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Dialog */}
      <SendToMechanicDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        busId={selectedBusId}
        busName={selectedBusName}
      />
    </div>
  );
}
